/**
 * React Query hooks for Leads activities.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { LeadsActivity, LeadsCreateActivityInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseActivitiesOptions {
  contactId?: string;
  companyId?: string;
  dealId?: string;
  activityType?: string;
  limit?: number;
}

function buildParams(options: UseActivitiesOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (options.contactId) params.contactId = options.contactId;
  if (options.companyId) params.companyId = options.companyId;
  if (options.dealId) params.dealId = options.dealId;
  if (options.activityType) params.activityType = options.activityType;
  if (options.limit !== undefined) params.limit = options.limit;
  return params;
}

export function useActivities(options: UseActivitiesOptions = {}) {
  const queryClient = useQueryClient();
  const filters = buildParams(options);

  const query = useQuery<{ activities: LeadsActivity[]; total: number }>({
    queryKey: queryKeys.leads.activities.list(filters),
    queryFn: () => api.listLeadsActivities(Object.keys(filters).length > 0 ? filters : undefined),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.leads.activities.all() });

  const createMutation = useMutation({
    mutationFn: (input: LeadsCreateActivityInput) => api.createLeadsActivity(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create activity', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLeadsActivity(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete activity', variant: 'destructive' }),
  });

  if (query.isError && !query.isFetching) {
    toast({ title: 'Error', description: 'Failed to load activities', variant: 'destructive' });
  }

  return {
    activities: query.data?.activities ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createActivity: (input: LeadsCreateActivityInput) => createMutation.mutateAsync(input),
    deleteActivity: (id: string) => deleteMutation.mutateAsync(id),
  };
}
